import { useCallback } from 'react';
import { auth } from './firebase';
import { confirmUserSignup } from './confirmUserSignup';
import { posthog } from '../services/telemetry';

interface ProvisionOverrides {
  /** Explicit user id (e.g. the freshly created `cred.user.uid`) when the
   * context value may still be stale right after sign-up. */
  userId?: string | null;
  /** Explicit email (e.g. the value typed into the form). */
  email?: string;
}

/**
 * Shared post-authentication side effects, used by every sign-in / sign-up
 * entry point (email/password screens and the OAuth `PostAuth` landing):
 *
 *  1. Provision the backend user (Mongo + Stripe) via `confirmUserSignup`.
 *     This is idempotent on the backend — a no-op for returning users.
 *  2. Identify the user in PostHog for funnel attribution.
 *
 * Failures are swallowed (logged only): backend provisioning is idempotent and
 * re-runs on the next sign-in, so it must never block navigation. The returned
 * function never throws.
 *
 * The user id is resolved from the explicit override first (most reliable right
 * after creation), then `auth.currentUser` — covering the case where the auth
 * context hasn't re-rendered yet.
 */
export function usePostAuthProvision() {
  return useCallback(
    async (overrides: ProvisionOverrides = {}): Promise<void> => {
      try {
        const token = await auth.currentUser?.getIdToken();
        if (token) await confirmUserSignup(token);

        const id = overrides.userId ?? auth.currentUser?.uid ?? null;
        if (id) {
          posthog.identify(id, {
            email: overrides.email ?? auth.currentUser?.email ?? undefined,
          });
        }
      } catch (err) {
        // Non-fatal: backend signup is idempotent and re-runs on next sign-in.
        console.warn('Post-auth provisioning failed:', err);
      }
    },
    []
  );
}
